/**
 * Shared filter-chip row for the Issues tabs — extracted from the inline
 * pattern duplicated across PortlessBlockedList / OnHoldList (rounded-full
 * pills, aria-pressed, trailing count at 70% opacity). The amber accent is the
 * byte-for-byte default matching those two lists; the purple accent mirrors
 * the Print Files tab's threadheads-purple accent language.
 *
 * Counts are optional and rendered only when > 0, exactly like the inline
 * originals. Page-reset / selection-clear side effects belong in the caller's
 * `onChange`.
 */
import { cn } from '@/lib/utils';

export interface IssueFilterChipOption<T extends string> {
  value: T;
  label: string;
  count?: number;
}

interface IssueFilterChipsProps<T extends string> {
  options: Array<IssueFilterChipOption<T>>;
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  accent?: 'amber' | 'purple';
}

export function IssueFilterChips<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  accent = 'amber',
}: IssueFilterChipsProps<T>) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={ariaLabel}>
      {options.map((chip) => {
        const active = value === chip.value;
        return (
          <button
            key={chip.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(chip.value)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-sm transition-colors motion-reduce:transition-none',
              accent === 'purple'
                ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-threadheads-purple/50 focus-visible:ring-offset-2'
                : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2',
              active
                ? accent === 'purple'
                  ? 'border-threadheads-purple bg-threadheads-purple/10 text-threadheads-purple dark:bg-threadheads-purple/20'
                  : 'border-amber-500 bg-amber-50 text-amber-900 dark:border-amber-500/60 dark:bg-amber-900/20 dark:text-amber-200'
                : 'border-border text-muted-foreground hover:bg-muted'
            )}
          >
            {chip.label}
            {(chip.count ?? 0) > 0 && <span className="ml-1.5 opacity-70">{chip.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
